import { Helmet } from 'react-helmet-async';
import {
	DEFAULT_DESCRIPTION,
	DEFAULT_OG_IMAGE,
	DEFAULT_TITLE,
	SITE_NAME,
	SITE_URL,
	TWITTER_HANDLE,
	absoluteUrl,
} from '../seo/siteConfig';

type SeoProps = {
	/** Page title. If omitted, site default is used. Rendered as "<title> | 05 Creation" unless it already contains the site name. */
	title?: string;
	/** Meta description, also used for og:description and twitter:description. */
	description?: string;
	/** Path of the current route (e.g. "/terms-and-conditions"). Used for canonical and og:url. Defaults to "/". */
	path?: string;
	/** Absolute or root-relative path to the social preview image. Defaults to /og-image-v3.jpg. */
	ogImage?: string;
	/** If true, emits `robots: noindex, nofollow`. Use on auth, signup, and any private route. */
	noindex?: boolean;
	/** Optional JSON-LD schema objects to emit as <script type="application/ld+json"> blocks. */
	jsonLd?: object | object[];
};

export function Seo({
	title,
	description = DEFAULT_DESCRIPTION,
	path = '/',
	ogImage = DEFAULT_OG_IMAGE,
	noindex = false,
	jsonLd,
}: SeoProps) {
	const fullTitle =
		!title || title === DEFAULT_TITLE
			? DEFAULT_TITLE
			: title.includes(SITE_NAME)
				? title
				: `${title} | ${SITE_NAME}`;
	const canonical = absoluteUrl(path);
	const ogImageUrl = absoluteUrl(ogImage);
	const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

	return (
		<Helmet>
			<title>{fullTitle}</title>
			<meta name="description" content={description} />
			<link rel="canonical" href={canonical} />
			{noindex && <meta name="robots" content="noindex, nofollow" />}

			{/* Open Graph */}
			<meta property="og:type" content="website" />
			<meta property="og:site_name" content={SITE_NAME} />
			<meta property="og:title" content={fullTitle} />
			<meta property="og:description" content={description} />
			<meta property="og:url" content={canonical} />
			<meta property="og:image" content={ogImageUrl} />
			<meta property="og:image:width" content="1200" />
			<meta property="og:image:height" content="630" />

			{/* Twitter */}
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:site" content={TWITTER_HANDLE} />
			<meta name="twitter:title" content={fullTitle} />
			<meta name="twitter:description" content={description} />
			<meta name="twitter:image" content={ogImageUrl} />

			{schemas.map((schema, i) => (
				<script key={i} type="application/ld+json">
					{JSON.stringify(schema)}
				</script>
			))}
		</Helmet>
	);
}

export { SITE_URL };
